import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../ui/card";
import { CheckCircle, Target, Timer, Gauge } from "lucide-react";

const PayrollKPIDashboard = ({ summary, entries, formatCurrency }) => {
  const { t } = useTranslation();
  const kpis = useMemo(() => {
    const totalEmployees = entries.length;
    const approvedCount = entries.filter((e) => e.is_approved).length;
    const pendingCount = totalEmployees - approvedCount;
    const avgSalary = totalEmployees > 0 ? (summary?.total_net || 0) / totalEmployees : 0;
    const approvalRate = totalEmployees > 0 ? (approvedCount / totalEmployees) * 100 : 0;

    return [
      {
        title: t("payrollUi.kpi.approvalRate"),
        value: `${Math.round(approvalRate)}%`,
        icon: Target,
        color: "text-green-400",
        bgColor: "bg-green-950/20",
        borderColor: "border-green-900/40",
      },
      {
        title: t("payrollUi.kpi.avgSalary"),
        value: formatCurrency(avgSalary),
        icon: Gauge,
        color: "text-primary",
        bgColor: "bg-primary/10",
        borderColor: "border-primary/40",
      },
      {
        title: t("payrollUi.kpi.pending"),
        value: pendingCount,
        icon: Timer,
        color: "text-amber-400",
        bgColor: "bg-amber-950/20",
        borderColor: "border-amber-900/40",
      },
      {
        title: t("payrollUi.kpi.approved"),
        value: approvedCount,
        icon: CheckCircle,
        color: "text-green-400",
        bgColor: "bg-green-950/20",
        borderColor: "border-green-900/40",
      },
    ];
  }, [summary, entries, formatCurrency, t]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {kpis.map((kpi, index) => (
        <Card
          key={kpi.title}
          className={`${kpi.bgColor} ${kpi.borderColor} border overflow-hidden transition-colors group cursor-pointer`}
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                  {kpi.title}
                </p>
                <div className="flex items-baseline gap-2">
                  <p className={`text-3xl font-bold ${kpi.color}`}>
                    {kpi.value}
                  </p>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${kpi.bgColor}`}>
                <kpi.icon className={`h-8 w-8 ${kpi.color}`} />
              </div>
            </div>
            <div className="mt-4 flex items-end justify-between h-8 gap-1">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className={`${kpi.color.replace("text", "bg")} opacity-30 rounded-sm transition-all duration-500 hover:opacity-70`}
                  style={{ height: `${24 + ((i + 1) * (index + 3) * 7) % 56}%`, width: "6px", animationDelay: `${index * 0.1 + i * 0.05}s` }}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default PayrollKPIDashboard;
